import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronRightIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MaskBadge } from "@/components/ui/mask-badge";
import { MasterVersionBadge } from "@/components/ui/master-version-spec-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { useMasks } from "@/hooks/useMasks";
import { useArchiveSpecList, useSpecLists } from "@/hooks/useSpecLists";
import { SpecListMinimal } from "@/types/specList";
import Tooltip from "@/components/shared/Tooltip";
import { GenericErrorDisplay } from "@/components/shared/ErrorDisplay";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { Main } from "@/components/shared/Main";

const checkSelectedMasterVersionMatches = (selected: string, list: SpecListMinimal): boolean => {
  return selected === list.masterVersion?.id || selected === list.latestVersion.id;
};

export function SpecListPage() {
  const navigate = useNavigate();

  const [selectedMask, setSelectedMask] = useState<string | undefined>(undefined);
  const [selectedMasterFile, setSelectedMasterFile] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set());
  const pageSize = 10;

  const {
    data: specLists,
    isLoading: isSpecListsLoading,
    error: isSpecListsError,
    mutate: refetchSpecListsData,
  } = useSpecLists();
  const { data: masks, isLoading: isMasksLoading, error: isMasksError } = useMasks();
  const { trigger: archiveSpecList, isMutating: isArchiving } = useArchiveSpecList();

  const totalCount = specLists.length;

  const totalPages = Math.ceil(totalCount / pageSize);

  const toggleRow = (specListId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(specListId)) {
      newExpanded.delete(specListId);
    } else {
      newExpanded.add(specListId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSpecListView = (specListId: string, specListVersionId: string) => {
    navigate(`/spec-lists/${specListId}/version/${specListVersionId}`);
  };

  const handleArchiveToggle = async (specList: SpecListMinimal) => {
    try {
      const successMessage = specList.isArchived
        ? `Successfully unarchived Spec List ${specList.name}.`
        : `Successfully archived Spec List ${specList.name}!`;

      await archiveSpecList(
        {
          id: specList.id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(successMessage);
            refetchSpecListsData();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  if (isMasksLoading || isSpecListsLoading) {
    return <LoadingDisplay />;
  }

  if (isMasksError || isSpecListsError) {
    return <GenericErrorDisplay />;
  }

  return (
    <Main className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Spec Lists</h1>
          <p className="text-muted-foreground">
            View and manage specification lists and their versions
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Master Version</label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedMasterFile === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMasterFile("all")}
                >
                  All Master Specs
                </Button>
                {specLists
                  .filter((list) => list.type === "master")
                  .map((list) => (
                    <Button
                      key={list.id}
                      variant={
                        checkSelectedMasterVersionMatches(selectedMasterFile, list)
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedMasterFile(list.masterVersion?.id ?? list.latestVersion.id)
                      }
                    >
                      {list.name}
                    </Button>
                  ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Mask</label>
              <div>
                <Combobox
                  className="w-48"
                  options={masks.map((m) => ({
                    key: m.id,
                    value: m.id,
                    label: m.name,
                  }))}
                  placeholder="All masks"
                  searchPlaceholder="Search masks"
                  value={selectedMask}
                  onChange={setSelectedMask}
                />
                <Button
                  className="ml-2"
                  disabled={!selectedMask}
                  onClick={() => setSelectedMask(undefined)}
                  variant="secondary"
                >
                  Clear filter
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
              <Label htmlFor="show-archived" className="text-sm font-medium">
                Show Archived
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              All Spec Lists
              {totalCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({totalCount} total, showing {(page - 1) * pageSize + 1}-
                  {Math.min(page * pageSize, totalCount)})
                </span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Master Version</TableHead>
                <TableHead>Latest Version</TableHead>
                <TableHead>Mask</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specLists
                .filter((list) =>
                  selectedMasterFile === "all"
                    ? true
                    : checkSelectedMasterVersionMatches(selectedMasterFile, list),
                )
                .filter((list) => (selectedMask ? list.mask?.id === selectedMask : true))
                .filter((list) => (showArchived ? true : !list.isArchived))
                .map((specList) => {
                  const reversedSpecListVersions = specList.versions.reverse();

                  return (
                    <Fragment key={specList.id}>
                      <TableRow
                        className={`hover:bg-muted/50 cursor-pointer ${specList.isArchived ? "opacity-60" : ""}`}
                        onClick={() =>
                          navigate(
                            `/spec-lists/${specList.id}/version/${specList.latestVersion.id}`,
                          )
                        }
                      >
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleRow(specList.id)}
                          >
                            {expandedRows.has(specList.id) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          {specList.name}
                          {specList.isArchived && (
                            <Badge variant="secondary" className="ml-2">
                              Archived
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {specList.type === "derived" ? (
                            <Badge variant="outline">Derived</Badge>
                          ) : null}
                          {specList.type === "master" ? (
                            <Badge variant="default">Master</Badge>
                          ) : null}
                          {specList.type === "custom" ? (
                            <Badge variant="outline">Custom</Badge>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {specList.masterVersion ? (
                            <MasterVersionBadge
                              masterName={`${specList.masterVersion.specList.name} - v${specList.masterVersion.version}`}
                              masterVersionId={specList.masterVersion.id}
                              masterId={specList.masterVersion.specList.id}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">v{specList.latestVersion.version}</Badge>
                        </TableCell>
                        <TableCell>
                          {specList.mask ? <MaskBadge maskName={specList.mask.name} /> : null}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end items-center gap-2">
                            <Tooltip
                              asChild
                              trigger={
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleArchiveToggle(specList)}
                                  disabled={isArchiving}
                                >
                                  {specList.isArchived ? (
                                    <ArchiveRestore className="h-4 w-4" />
                                  ) : (
                                    <Archive className="h-4 w-4" />
                                  )}
                                </Button>
                              }
                              content={specList.isArchived ? "Unarchive" : "Archive"}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(specList.id) && (
                        <TableRow key={`${specList.id}-expanded`}>
                          <TableCell colSpan={9} className="p-0">
                            <div className="bg-muted/30 p-4">
                              <h4 className="font-medium mb-3">Spec List Versions</h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Version</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead>Created At</TableHead>
                                    <TableHead />
                                    <TableHead />
                                    <TableHead />
                                    <TableHead />
                                    <TableHead />
                                    <TableHead className="text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {reversedSpecListVersions.map((version) => (
                                    <TableRow
                                      key={version.id}
                                      className="hover:bg-muted/50 cursor-pointer"
                                      onClick={() => handleSpecListView(specList.id, version.id)}
                                    >
                                      <TableCell>
                                        <Badge variant="outline">v{version.version}</Badge>
                                      </TableCell>
                                      <TableCell>{version.createdBy}</TableCell>
                                      <TableCell>
                                        {new Date(version.createdAt).toLocaleDateString()}
                                      </TableCell>
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell className="text-right" />
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
            </TableBody>
          </Table>

          {specLists.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No spec lists found matching the current filters.
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Main>
  );
}
